// Copyright (c) Move Closer 2022.
import {
  IConnector,
  Injectable,
  IResponse,
  mapModel,
  ResourceActionFailed
} from '@movecloser/front-core'

import { IGraphQL } from '../../../contexts'
import { resolveFromStatus } from '../../../support'

import { UserData, UserModel } from '../../auth/shared'

import {
  Agreements,
  ChangeEmailPayload,
  ChangePasswordPayload,
  ChangeUserDetailsPayload,
  DeliveryAddressPayload,
  IProfileService
} from '../contracts'
import { User } from '../models/user'
import { userAdapterMap } from '../models/user.adapter'
import { addressAdapterMap } from '../models/address.adapter'

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 */
@Injectable()
export class ProfileService implements IProfileService {
  protected graphQlConnector: IGraphQL
  protected readonly connector: IConnector
  protected readonly agreementsMap: Record<string, string> | null

  constructor (graphQlConnector: IGraphQL, connector: IConnector, agreementsMap: Record<string, string> | null) {
    this.graphQlConnector = graphQlConnector
    this.connector = connector
    this.agreementsMap = agreementsMap
  }

  /**
   * @inheritDoc
   */
  public async changeEmail (payload: ChangeEmailPayload): Promise<UserModel> {
    const response: IResponse = await this.graphQlConnector.call('changeEmailMutation', payload)

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return this.hydrateUser(response.data.updateCustomer.customer)
  }

  /**
   * @inheritDoc
   */
  public async changePassword (payload: ChangePasswordPayload): Promise<void> {
    const response: IResponse = await this.graphQlConnector.call('changePasswordMutation', payload)

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }
  }

  /**
   * @inheritDoc
   */
  public async changeUserDetails (payload: ChangeUserDetailsPayload): Promise<UserModel> {
    const response: IResponse = await this.graphQlConnector.call('changeUserDetailsMutation', payload)

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return this.hydrateUser(response.data.updateCustomer.customer)
  }

  /**
   * @inheritDoc
   */
  public async getUserDetails (): Promise<UserModel> {
    const response: IResponse = await this.graphQlConnector.call('customerQuery', {})

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return this.hydrateUser(response.data.customer)
  }

  /**
   * @inheritDoc
   */
  public async getUserSubscriptions (): Promise<Agreements> {
    const response: IResponse = await this.connector.call('me', 'subscriptions')

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return {
      email: response.data.data.email,
      agreements: Object.entries(response.data.data.agreements as Agreements['agreements']).reduce((acc, [key, value]) => {
        if (!this.agreementsMap || !(key in this.agreementsMap)) {
          return { ...acc, [key]: value }
        }

        return {
          ...acc,
          [this.agreementsMap[key]]: value
        }
      }, {})
    }
  }

  /**
   * Adds new delivery address.
   */
  public async storeDeliveryAddress (payload: DeliveryAddressPayload): Promise<number> {
    let mappedPayload = payload
    if (Object.prototype.hasOwnProperty.call(payload, 'region')) {
      mappedPayload = mapModel(payload, addressAdapterMap)
    }

    const response: IResponse = await this.graphQlConnector.call('storeDeliveryAddressQuery', mappedPayload)

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return response.data.createCustomerAddress.id
  }

  /**
   * Adds new delivery address.
   */
  public async updateDeliveryAddress (
    addressId: number,
    payload: Partial<DeliveryAddressPayload>
  ): Promise<number> {
    let mappedPayload = payload
    if (Object.prototype.hasOwnProperty.call(payload, 'region')) {
      mappedPayload = mapModel(payload, addressAdapterMap)
    }

    const response: IResponse = await this.graphQlConnector.call(
      'updateDeliveryAddressQuery',
      { addressId, ...mappedPayload }
    )

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return response.data.updateCustomerAddress.id
  }

  /**
   * @inheritDoc
   */
  public async subscribeNewsletter (isSubscribedNewsletter: boolean): Promise<UserModel> {
    const response: IResponse = await this.graphQlConnector.call(
      'subscribeNewsletterMutation',
      { isSubscribedNewsletter }
    )

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return this.hydrateUser(response.data.updateCustomer.customer)
  }

  protected hydrateUser (userData: UserData): UserModel {
    return User.hydrate<UserModel>(mapModel<UserModel>(userData, userAdapterMap, false))
  }
}
